import { useCallback } from 'react'
import { useChatStore } from '@/store/chatStore'
import { axiosInstance } from '@/services/api'
import type { ChatSession, Message, SessionResponse, MessageResponse } from '@/types'

const PAGE_SIZE = 20

// Pagination page is module-level — persists across re-renders without causing re-renders itself.
// hasMore and isLoadingMore live in Zustand so all components share the same value.
let currentPage = 1

export function useChatHistory() {
  const {
    config,
    sessions,
    hasMore,
    isLoadingMore,
    setSessions,
    setHasMore,
    setIsLoadingMore,
    setMessages,
    loadHistorySession,
    setIsLoadingHistory,
    closeSidebar,
  } = useChatStore()

  // Load page 1 — called every time history sidebar opens.
  // Resets pagination and replaces the sessions list.
 const loadSessions = useCallback(async () => {
  if (!config) return

  try {
    const { tenantId } = useChatStore.getState()
    const response = await axiosInstance.get('/api/chat/sessions', {
      params: { tenantId, domainId: config.domainId, page: 1, pageSize: PAGE_SIZE }
    })

    const data = response.data?.items ?? response.data
    if (!Array.isArray(data)) {
      console.warn('[History] Unexpected sessions response shape:', response.data)
      return
    }

    const totalPages: number = response.data?.totalPages ?? 1

    const mapped: ChatSession[] = data.map((s: SessionResponse) => ({
      id:              s.id,
      title:           s.title,
      domainId:        s.domainId,
      createdAt:       new Date(s.createdAt),
      totalTokensUsed: s.totalTokensUsed,
    }))

    // Merge fetched sessions with any optimistically-added sessions already in the store.
    // This prevents the active session (added locally after first message) from being
    // wiped when the history sidebar fetches page 1 before the backend has committed it,
    // or when the fetch races with the DB write.
    // Guard: only keep local sessions for this exact domain — prevents cross-domain bleed.
    const { sessions: existingSessions } = useChatStore.getState()
    const fetchedIds = new Set(mapped.map(s => s.id))
    const localOnly = existingSessions.filter(
      s => !fetchedIds.has(s.id) && s.domainId === config.domainId
    )

    // Local-only sessions go at the top — they are always the most recent
    setSessions([...localOnly, ...mapped])
    currentPage = 1
    setHasMore(totalPages > 1)
  } catch (err) {
    console.error('[History] Failed to load sessions:', err)
  }
}, [config])

  // Load next page — appends to existing sessions list
  const loadMoreSessions = useCallback(async () => {
    if (!config || isLoadingMore || !hasMore) return

    setIsLoadingMore(true)
    try {
      const { tenantId } = useChatStore.getState()
      const nextPage = currentPage + 1

      const response = await axiosInstance.get('/api/chat/sessions', {
        params: { tenantId, domainId: config.domainId, page: nextPage, pageSize: PAGE_SIZE }
      })

      const data = response.data?.items ?? response.data
      if (!Array.isArray(data)) {
        console.warn('[History] Unexpected sessions response shape:', response.data)
        return
      }

      const totalPages: number = response.data?.totalPages ?? 1

      const mapped: ChatSession[] = data.map((s: SessionResponse) => ({
        id:              s.id,
        title:           s.title,
        domainId:        s.domainId,
        createdAt:       new Date(s.createdAt),
        totalTokensUsed: s.totalTokensUsed,
      }))

      // Append — deduplicate by ID to prevent duplicates on rapid clicks
      const existingIds = new Set(sessions.map(s => s.id))
      const newSessions = mapped.filter(s => !existingIds.has(s.id))
      setSessions([...sessions, ...newSessions])

      currentPage = nextPage
      setHasMore(nextPage < totalPages)
    } catch (err) {
      console.error('[History] Failed to load more sessions:', err)
    } finally {
      setIsLoadingMore(false)
    }
  }, [config, hasMore, isLoadingMore, sessions])

  // Load messages for a specific session and switch to it
  const loadSession = useCallback(async (sessionId: string) => {
    if (!config) return

    setIsLoadingHistory(true)
    try {
      const response = await axiosInstance.get(
        `/api/chat/sessions/${sessionId}/messages`
      )

      const payload = response.data?.data ?? response.data
      const data = payload?.items ?? payload

      if (!Array.isArray(data)) {
        console.warn('[History] Unexpected messages response shape:', response.data)
        return
      }

      const messages: Message[] = data
        .filter((m: MessageResponse) => m.role !== 0)
        .map((m: MessageResponse) => ({
          id:        m.id,
          role:      m.role === 1 ? 'user' : 'assistant',
          content:   m.content,
          timestamp: new Date(m.createdAt),
        }))

      setMessages(messages)
      loadHistorySession(sessionId)
      closeSidebar()
    } catch (err) {
      console.error('[History] Failed to load session messages:', err)
    } finally {
      setIsLoadingHistory(false)
    }
  }, [config])

  return {
    loadSessions,
    loadMoreSessions,
    loadSession,
    hasMore,
    isLoadingMore,
  }
}